import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LogOut from 'lucide-react/dist/esm/icons/log-out'

import { useAuth } from '@/contexts/AuthContext'
import { LogoutConfirmDialog } from '@/components/layout/LogoutConfirmDialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatPrivateDisplayName } from '@/utils/format'

interface AppHeaderProps {
  /** When true, shows a skeleton pill while the risk-assessment profile is still loading. */
  showUserSkeleton?: boolean
}

export function AppHeader({ showUserSkeleton = false }: AppHeaderProps) {
  const { logout, riskUser, user } = useAuth()
  const navigate = useNavigate()
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true)
    try {
      await logout()
      navigate('/login', { replace: true })
    } finally {
      setIsLoggingOut(false)
      setLogoutDialogOpen(false)
    }
  }

  const showProfileSkeleton = showUserSkeleton || (!!user && !riskUser)

  const initials = riskUser
    ? `${riskUser.firstName?.[0] ?? ''}${riskUser.lastName?.[0] ?? ''}`.toUpperCase()
    : ''

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        <img src="/mezza_wordmark.png" alt="Mezza" className="h-5 w-auto" width={101} height={18} />

        <div className="flex h-9 items-center gap-2 sm:gap-3">
          {showProfileSkeleton ? (
            <Skeleton className="h-9 w-28 rounded-full sm:w-36" />
          ) : riskUser ? (
            <div className="box-border flex h-9 items-center gap-2.5 rounded-full border border-border/60 bg-white pl-1 pr-3 shadow-sm">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {initials}
              </span>
              <span className="hidden text-sm font-semibold text-foreground sm:inline">
                {formatPrivateDisplayName(riskUser.firstName, riskUser.lastName)}
              </span>
            </div>
          ) : null}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLogoutDialogOpen(true)}
            aria-label="Log out"
            className="text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <LogoutConfirmDialog
        open={logoutDialogOpen}
        onOpenChange={setLogoutDialogOpen}
        onConfirm={handleLogoutConfirm}
        isLoggingOut={isLoggingOut}
      />
    </header>
  )
}
